import { Badge } from '@/components/ui';
import { cn } from '@/utils/cn';

import { groupCatalogue } from './roles';

/**
 * Grouped checkboxes over the permission catalogue.
 *
 * `baseline` is what the selection is compared with -- the role's set when
 * editing a member -- so every difference is labelled "Added" or "Removed"
 * and an admin can see at a glance what makes this person unlike their role.
 */
export function PermissionChecklist({ catalogue, selected, onToggle, baseline, disabled }) {
  const groups = groupCatalogue(catalogue);

  return (
    // Columns rather than a grid: a grid row is as tall as its longest group,
    // which leaves a short group like Company sitting above a block of blank space.
    <div className="gap-x-6 sm:columns-2">
      {groups.map((group) => (
        <fieldset key={group.group} className="mb-4 min-w-0 break-inside-avoid" disabled={disabled}>
          <legend className="text-ink mb-1 text-sm font-semibold">{group.group}</legend>
          <ul className="space-y-0.5">
            {group.items.map((entry) => {
              const checked = selected.has(entry.code);
              const inBaseline = baseline?.has(entry.code);
              const added = baseline && checked && !inBaseline;
              const removed = baseline && !checked && inBaseline;
              return (
                <li key={entry.code}>
                  <label
                    className={cn(
                      'flex items-start gap-2 rounded-lg px-2 py-1 text-sm leading-snug transition',
                      disabled
                        ? 'cursor-not-allowed opacity-70'
                        : 'hover:bg-surface-muted cursor-pointer',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="border-line text-brand-600 focus:ring-brand-500 mt-0.5 size-4 shrink-0 rounded border"
                      checked={checked}
                      onChange={() => onToggle(entry.code)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className={cn('text-ink', removed && 'text-ink-muted line-through')}>
                        {entry.description}
                      </span>
                      <span className="text-ink-muted block font-mono text-[11px]">
                        {entry.code}
                      </span>
                    </span>
                    {added && <Badge tone="success">Added</Badge>}
                    {removed && <Badge tone="danger">Removed</Badge>}
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      ))}
    </div>
  );
}
